// E-2/E-3/E-4 — LiftDiagram.tsx: true-scale SVG render from the scene graph ONLY
// (blueprint §5.1/§5.2). Never re-derives math — buildScene output in, SVG out.
// Figure-art style: black line-art on white, CAPS leader labels, dashed tag
// lines, ONE downward gravity arrow, minimal arrowheads, NO color except
// force-overlay callouts (E-3 — the ONLY place color is allowed). E-4 chrome:
// ≤4 ft dimension green/red, dotted 10°/70° limit arcs, refusal hatch markers.
import type { LiftDetail, LiftComputed } from '@/types/planData';
import type { Scene, SceneAnnotation, ScenePoint } from '../../utils/sceneGraph';
import { pxPerFt } from '../../utils/sceneGraph';
import { DiagramNode } from './DiagramNode';
import { towerSilhouette } from './blockArt';

export interface LiftDiagramProps {
  lift: LiftDetail;
  scene: Scene;
  computed?: LiftComputed;
  /** E-3: live force overlay — computed forces as callout labels; ONLY color zone */
  forceOverlay?: boolean;
  /** E-3: drag handles on movable nodes (editor wraps this; default off for read-only) */
  onDragPoint?: (nodeId: string, scenePt: { x: number; y: number }) => void;
  width?: number;
  height?: number;
  className?: string;
  gridTickFt?: 25 | 50;
}

const NODE_LABELS: Record<string, string> = {
  topBlock: 'TOP BLOCK',
  heelBlock: 'HEEL BLOCK',
  jumpBlock: 'JUMP BLOCK',
  redirectBlock: 'IDLER/REDIRECT BLOCK',
  mountBlock: 'MOUNT BLOCK',
  winch: 'WINCH (TRUCK)',
  load: 'LOAD',
  tagAnchor: 'TAG LINE HOLD',
  trolley: 'TROLLEY BLOCK',
  highlineShaftAnchor: 'REDUNDANT RIGGING SLING',
  staticUpperAnchor: 'STATIC LINE ANCHOR',
};

const DIM_OK = '#2e7d32';   // green — §4.4.4.1 ≤4 ft pass
const DIM_FAIL = '#b71c1c'; // red — exceeds 4 ft / refusal

export function LiftDiagram({ lift, scene, computed, forceOverlay = false, width = 600, height = 600, className, gridTickFt = 25 }: LiftDiagramProps) {
  const f = pxPerFt(scene, width, height);
  const bb = scene.boundingBoxFt;
  const spanX = (bb.maxX - bb.minX) * f;
  const spanY = (bb.maxY - bb.minY) * f;
  const offX = (width - spanX) / 2 - bb.minX * f;
  const offY = height - (height - spanY) / 2; // y-up scene → y-down px
  const toPx = (pt: { x: number; y: number }) => ({ x: offX + pt.x * f, y: offY - pt.y * f });

  const sil = towerSilhouette(scene.structure.type, scene.structure.heightFt, scene.structure.faceWidthFt);

  const byId = new Map(scene.points.map((p) => [p.id, p]));
  const gridLines: { x1: number; y1: number; x2: number; y2: number; label?: string }[] = [];
  for (let x = Math.ceil(bb.minX / gridTickFt) * gridTickFt; x <= bb.maxX; x += gridTickFt) {
    const a = toPx({ x, y: bb.minY });
    const b = toPx({ x, y: bb.maxY });
    gridLines.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y, label: `${x}` });
  }
  for (let y = Math.ceil(bb.minY / gridTickFt) * gridTickFt; y <= bb.maxY; y += gridTickFt) {
    const a = toPx({ x: bb.minX, y });
    const b = toPx({ x: bb.maxX, y });
    gridLines.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y, label: `${y}` });
  }

  const tagMethod = lift.geometry.tag.method;
  const refused = computed?.status === 'refused';

  return (
    <svg
      data-testid="lift-diagram"
      data-scenario={lift.scenario}
      data-tag-method={tagMethod}
      data-tower-type={scene.structure.type}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className ?? "lift-diagram"}
    >
      <defs>
        {/* refusal hatch pattern (E-3) — red hatching, no numbers */}
        <pattern id="refusal-hatch" patternUnits="userSpaceOnUse" width={8} height={8} patternTransform="rotate(45)">
          <rect width={8} height={8} fill="white" />
          <line x1={0} y1={0} x2={0} y2={8} stroke={DIM_FAIL} strokeWidth={2.5} />
        </pattern>
      </defs>

      {/* grid (25/50 ft ticks labeled) */}
      <g className="diagram-grid" stroke="#d8d8d8" strokeWidth={0.5}>
        {gridLines.map((l, i) => (
          <line key={`g${i}`} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} />
        ))}
      </g>

      {/* tower silhouette — type-specific (§5.2.3a): authored in scene ft,
          rendered through ONE scale(f,-f) group + non-scaling strokes */}
      <g
        className="tower-silhouette"
        fill="none"
        stroke="black"
        strokeWidth={1.25}
        transform={`translate(${offX} ${offY}) scale(${f} ${-f})`}
      >
        {sil.paths.map((d, i) => (
          <path key={i} d={d} vectorEffect="non-scaling-stroke" />
        ))}
      </g>

      {/* segments */}
      <g className="diagram-segments" fill="none" stroke="black" strokeWidth={1.25}>
        {scene.segments.map((s) => {
          const a = byId.get(s.from);
          const b = byId.get(s.to);
          if (!a || !b) return null;
          const pa = toPx(a.pt);
          const pb = toPx(b.pt);
          return (
            <line
              key={s.id}
              data-segment={s.kind}
              x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y}
              strokeDasharray={s.style === 'dashed' ? '6 4' : undefined}
            />
          );
        })}
      </g>

      {/* annotations: graphics only (arcs, gravity arrow); text goes through
          the collision-resolved label layer below */}
      <g className="diagram-annotations">
        {scene.annotations.map((a) => (
          <AnnotationMark key={a.id} a={a} toPx={toPx} />
        ))}
      </g>

      {/* refusal markers (E-3): red-hatched at the relevant node, NO numbers */}
      {refused && (
        <g className="refusal-markers">
          {scene.annotations.filter((a) => a.kind === 'refusalMarker').map((a) => (
            <RefusalMarker key={a.id} a={a} toPx={toPx} />
          ))}
        </g>
      )}

      {/* nodes — art only; CAPS labels join the resolved label layer */}
      <g className="diagram-nodes">
        {scene.points.map((p) => (
          <DiagramNode key={p.id} point={p} toPx={toPx} label={undefined} />
        ))}
      </g>

      {/* force-overlay leader lines (text handled by the label layer) */}
      {forceOverlay && !refused && computed && (
        <ForceLeaders computed={computed} scene={scene} toPx={toPx} />
      )}

      {/* ONE collision-resolved text layer — nodes, annotations, force callouts */}
      <g className="resolved-labels">
        {resolveLabelPositions(buildLabelItems(scene, computed, forceOverlay && !refused, toPx, lift.loading.grossLoadLbs), width).map((l) => (
          <text
            key={l.id}
            x={l.x}
            y={l.y}
            fontSize={l.fs}
            fontFamily="monospace"
            fill={l.color}
            paintOrder="stroke"
            stroke="#ffffff"
            strokeWidth={2.5}
            style={{ letterSpacing: '0.4px' }}
          >
            {l.text}
          </text>
        ))}
      </g>
    </svg>
  );
}


// ---------------------------------------------------------------------------
// Label collision resolution (Brian 2026-09-08: "the words are still jumbled").
// ALL diagram text goes through ONE pass: labels are collected from
// annotations + force callouts, then nudged until no two overlap and nothing
// spills off the right edge. Halo stroke keeps them legible over lines.
// ---------------------------------------------------------------------------
interface LabelItem {
  id: string;
  text: string;
  x: number;
  y: number;
  color: string;
  fs: number;
}

function resolveLabelPositions(labels: LabelItem[], width: number): LabelItem[] {
  const placed: { x: number; y: number; w: number; h: number }[] = [];
  const out = [...labels].sort((a, b) => a.y - b.y || a.x - b.x);
  for (const l of out) {
    const w = l.text.length * l.fs * 0.62 + 4;
    const h = l.fs + 3;
    let x = l.x;
    let y = l.y;
    if (x + w > width - 2) x = Math.max(2, width - w - 2);
    let guard = 0;
    const overlaps = (yy: number) =>
      placed.some((r) => yy - h < r.y && yy > r.y - r.h && x < r.x + r.w && x + w > r.x);
    while (overlaps(y) && guard++ < 60) y += h;
    l.x = x;
    l.y = y;
    placed.push({ x, y, w, h });
  }
  return out;
}

function AnnotationMark({ a, toPx }: { a: SceneAnnotation; toPx: (pt: { x: number; y: number }) => { x: number; y: number } }) {
  const p = toPx(a.at);
  if (a.kind === 'limitArc') {
    // E-4: dotted limit arcs — 10°/70° from vertical (IV(f) triggers)
    const r = 46;
    const rad = ((a.valueDeg ?? 0) * Math.PI) / 180;
    // arc sweeps from vertical (up) toward +x
    const ex = p.x + r * Math.sin(rad);
    const ey = p.y - r * Math.cos(rad);
    const label = `${a.valueDeg}° LIMIT (IV(f))`;
    return (
      <g className="limit-arc">
        <path
          d={`M ${p.x} ${p.y - r} A ${r} ${r} 0 0 1 ${ex} ${ey}`}
          fill="none" stroke="black" strokeWidth={0.75} strokeDasharray="2 3"
        />
        {/* label handled by the collision-resolved layer */}
      </g>
    );
  }
  if (a.kind === 'angleArc' || a.kind === 'label' || a.kind === 'dimension' || a.kind === 'refusalMarker') {
    // Text rendered by the collision-resolved label layer — graphics only here.
    return null;
  }
  if (a.kind === 'forceArrow') {
    // ONE downward gravity arrow off the load + minimal arrowhead
    return (
      <g className="gravity-arrow">
        <line x1={p.x} y1={p.y + 14} x2={p.x} y2={p.y + 40} stroke="black" strokeWidth={1.25} />
        <polygon points={`${p.x - 3},${p.y + 34} ${p.x + 3},${p.y + 34} ${p.x},${p.y + 42}`} fill="black" />
        {/* label handled by the collision-resolved layer */}
      </g>
    );
  }
  return null;
}

function RefusalMarker({ a, toPx }: { a: SceneAnnotation; toPx: (pt: { x: number; y: number }) => { x: number; y: number } }) {
  const p = toPx(a.at);
  const s = 9;
  return (
    <g data-testid="refusal-marker" className="refusal-marker">
      <rect x={p.x - s} y={p.y - s} width={2 * s} height={2 * s} fill="url(#refusal-hatch)" stroke={DIM_FAIL} strokeWidth={1} />
      {a.text && (
        <text x={p.x + s + 4} y={p.y - s} fontSize={8} fontFamily="monospace" fill={DIM_FAIL}>
          {a.text.toUpperCase()}
        </text>
      )}
    </g>
  );
}

// Leader lines from each force node to its (resolved) callout text.
function ForceLeaders({ computed, scene, toPx }: {
  computed: LiftComputed;
  scene: Scene;
  toPx: (pt: { x: number; y: number }) => { x: number; y: number };
}) {
  const ids = forceCalloutNodeIds(computed, scene);
  return (
    <g className="force-overlay">
      {ids.map((id) => {
        const pt = scene.points.find((p) => p.id === id);
        if (!pt) return null;
        const q = toPx(pt.pt);
        return <line key={`fl-${id}`} x1={q.x} y1={q.y} x2={q.x + 10} y2={q.y - 12} stroke="#1a237e" strokeWidth={0.75} />;
      })}
    </g>
  );
}

function forceCalloutNodeIds(computed: LiftComputed, scene: Scene): string[] {
  const f = computed.forces;
  const ids: string[] = ['winch'];
  if (scene.points.some((p) => p.id === 'topBlock')) ids.push('topBlock');
  if (f.heelBlockLbs != null) ids.push('heelBlock');
  if (f.jumpBlockLbs != null) ids.push('jumpBlock');
  if (f.redirectBlockLbs != null) ids.push('redirectBlock');
  if (f.mountBlockLbs != null) ids.push('mountBlock');
  if (f.tagLineLbs != null) ids.push('tagAnchor');
  if (f.staticLineTensionLbs != null) ids.push('staticUpperAnchor');
  return ids;
}

/** Collect EVERY text label in the diagram into one list for collision resolution. */
function buildLabelItems(
  scene: Scene,
  computed: LiftComputed | undefined,
  withForces: boolean,
  toPx: (pt: { x: number; y: number }) => { x: number; y: number },
  grossLoadLbs: number,
): LabelItem[] {
  const items: LabelItem[] = [];
  // node CAPS labels — when the force overlay is on, the callout already names
  // the component ("TOP BLOCK 1721.1 LB"), so skip the bare node label to avoid
  // the doubled text the vision review flagged (TOP BLOCK + TOP BLOCK xxx LB).
  const coveredByForce = withForces
    ? new Set(['topBlock', 'load', 'tagAnchor', 'winch', 'heelBlock', 'jumpBlock', 'redirectBlock', 'mountBlock', 'staticUpperAnchor'])
    : new Set<string>();
  for (const p of scene.points) {
    const label = NODE_LABELS[p.id];
    if (!label || coveredByForce.has(p.id)) continue;
    const q = toPx(p.pt);
    items.push({ id: `node-${p.id}`, text: label.toUpperCase(), x: q.x + 10, y: q.y - 10, color: 'black', fs: 9 });
  }
  // annotation text (angles, labels, dimensions, limit arcs, gravity arrow, refusals)
  for (const a of scene.annotations) {
    if (!a.text) continue;
    if (withForces && a.id === 'top-force') continue; // force callout covers it — no double text

    const q = toPx(a.at);
    if (a.kind === 'limitArc') {
      const r = 46;
      const rad = ((a.valueDeg ?? 0) * Math.PI) / 180;
      items.push({
        id: a.id,
        text: a.text.toUpperCase(),
        x: q.x + r * Math.sin(rad) + 4,
        y: q.y - r * Math.cos(rad),
        color: 'black',
        fs: 7.5,
      });
    } else if (a.kind === 'forceArrow') {
      items.push({ id: a.id, text: a.text.toUpperCase(), x: q.x + 8, y: q.y + 34, color: 'black', fs: 8.5 });
    } else if (a.kind === 'refusalMarker') {
      items.push({ id: a.id, text: a.text.toUpperCase(), x: q.x + 13, y: q.y - 9, color: DIM_FAIL, fs: 8 });
    } else {
      const color = a.kind === 'dimension' && a.state === 'fail' ? DIM_FAIL : a.kind === 'dimension' && a.state === 'ok' ? DIM_OK : 'black';
      items.push({ id: a.id, text: a.text.toUpperCase(), x: q.x + 6, y: q.y - 6, color, fs: 8.5 });
    }
  }
  // force callouts
  if (withForces && computed) {
    const f = computed.forces;
    const at = (id: string) => {
      const pt = scene.points.find((p) => p.id === id);
      return pt ? toPx(pt.pt) : null;
    };
    const add = (id: string, nodeId: string, text: string, dy: number) => {
      const q = at(nodeId);
      if (q) items.push({ id, text: text.toUpperCase(), x: q.x + 12, y: q.y + dy, color: '#1a237e', fs: 8 });
    };
    add('fo-top', 'topBlock', `TOP BLOCK ${f.topBlockResultantLbs.toFixed(1)} LB`, -26);
    add('fo-line', 'winch', `LINE ${f.linePartTensionLbs.toFixed(1)} LB`, -26);
    add('fo-pull', 'winch', `HOIST PULL ${f.winchPullLbs.toFixed(1)} LB`, -14);
    if (f.heelBlockLbs != null) add('fo-heel', 'heelBlock', `HEEL ${f.heelBlockLbs.toFixed(1)} LB`, -14);
    if (f.jumpBlockLbs != null) add('fo-jump', 'jumpBlock', `JUMP ${f.jumpBlockLbs.toFixed(1)} LB`, -14);
    if (f.redirectBlockLbs != null) add('fo-redirect', 'redirectBlock', `REDIRECT ${f.redirectBlockLbs.toFixed(1)} LB`, -14);
    if (f.mountBlockLbs != null) add('fo-mount', 'mountBlock', `MOUNT ${f.mountBlockLbs.toFixed(1)} LB`, -14);
    if (f.tagLineLbs != null) add('fo-tag', 'tagAnchor', `TAG LINE HOLD ${f.tagLineLbs.toFixed(1)} LB`, -14);
    add('fo-load', 'load', `LOAD ${grossLoadLbs} LB`, 22);
    if (f.staticLineTensionLbs != null) add('fo-static', 'staticUpperAnchor', `STATIC ${f.staticLineTensionLbs.toFixed(1)} LB`, -14);
    if (f.slingLegLbs != null) add('fo-sling', 'load', `SLING LEG ${f.slingLegLbs.toFixed(1)} LB`, 16);
  }
  return items;
}

/** E-3 editor export: point kind used by drag handle placement. */
export function draggableScenePoint(p: ScenePoint): boolean {
  return ['load', 'tagAnchor', 'heelBlock', 'jumpBlock', 'redirectBlock', 'mountBlock', 'topBlock', 'trolley']
    .includes(p.id);
}
